import React, { useState, useEffect, useRef } from "react";
import styles from "./benefits.module.scss";
import { cn } from "utils";
import Input from "components/Input";
import Select from "components/Select";
import { Button } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCompanyBenefit, updateCompanyBenefit, getCompanyBenefit } from "services/benefits";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../axios/axiosInstance";

const BENEFIT_TYPES = [
    { value: "Medical",      label: "Medical" },
    { value: "Dental",       label: "Dental" },
    { value: "Vision",       label: "Vision" },
    { value: "401k",         label: "401(k)" },
    { value: "HSA",          label: "HSA" },
    { value: "HealthFSA",    label: "Health FSA" },
    { value: "DependentFSA", label: "Dependent Care FSA" },
    { value: "LifeInsurance",label: "Life Insurance" },
    { value: "STD",          label: "Short-Term Disability" },
    { value: "LTD",          label: "Long-Term Disability" },
    { value: "Other",        label: "Other" },
];

const BENEFIT_PERIODS = [
    { value: "Weekly",      label: "Weekly" },
    { value: "BiWeekly",    label: "Bi-Weekly" },
    { value: "SemiMonthly", label: "Semi-Monthly" },
    { value: "Monthly",     label: "Monthly" },
    { value: "Annually",    label: "Annually" },
];

const WAITING_PERIODS = [
    { value: "Immediate",             label: "Immediate (day of hire)" },
    { value: "FirstOfMonthAfterHire", label: "1st of month after hire date" },
    { value: "30days",                label: "30 days" },
    { value: "60days",                label: "60 days" },
    { value: "90days",                label: "90 days (ACA maximum)" },
];

const toDateInput = (val) => { if (!val) return ''; return val.substring(0, 10); };

const DOC_TYPES = [
    { value: 'SBC',              label: 'Summary of Benefits & Coverage (SBC)' },
    { value: 'PlanDoc',          label: 'Plan Document / Certificate' },
    { value: 'EnrollmentGuide',  label: 'Enrollment Guide' },
    { value: 'NetworkDirectory', label: 'Network Directory' },
    { value: 'Formulary',        label: 'Drug Formulary' },
    { value: 'Other',            label: 'Other' },
];

const initialForm = {
    description: "", type: "", period: "", effectiveStart: "", effectiveEnd: "",
    companyContributionAmount: "", companyContributionPercent: "",
    employeeContributionAmount: "", employeeContributionPercent: "",
    totalPremiumAmount: "",
    preTax: true, waitingPeriod: "FirstOfMonthAfterHire",
    notes: "",
};

const ContribToggle = ({ mode, onToggle }) => (
    <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #dee2e6', marginBottom: 6 }}>
        <button type="button"
            style={{ padding: '4px 14px', background: mode === 'amount' ? '#1B75F0' : '#fff', color: mode === 'amount' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer' }}
            onClick={() => onToggle('amount')}>$ Amount</button>
        <button type="button"
            style={{ padding: '4px 14px', background: mode === 'percent' ? '#1B75F0' : '#fff', color: mode === 'percent' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', borderLeft: '1px solid #dee2e6', cursor: 'pointer' }}
            onClick={() => onToggle('percent')}>% Percent</button>
    </div>
);

const AddEditCompanyBenefit = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const benefitId = searchParams.get('id');
    const isEditMode = !!benefitId;

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [companyMode, setCompanyMode] = useState('amount');
    const [employeeMode, setEmployeeMode] = useState('amount');
    const [documents, setDocuments] = useState([]); // [{ type, url, name }]
    const [newDocType, setNewDocType] = useState('SBC');
    const [newDocUrl, setNewDocUrl] = useState('');
    const [newDocName, setNewDocName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);

    const companyId = () => localStorage.getItem("companyId") || '';

    const handleFileUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        setUploadError('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('documentType', 'benefit_plan');
            const resp = await axiosInstance.post(
                `/companies/${companyId()}/documents`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const rawUrl = resp.data?.fileUrl || resp.data?.url || '';
            // Convert relative path to absolute using the API base URL
            const fileUrl = rawUrl.startsWith('http')
                ? rawUrl
                : new URL(rawUrl, axiosInstance.defaults.baseURL).href.replace('/api/uploads/', '/uploads/');
            setNewDocUrl(fileUrl);
            if (!newDocName) setNewDocName(file.name.replace(/\.[^/.]+$/, ''));
        } catch {
            setUploadError('Upload failed. Try again or paste a URL manually.');
        } finally {
            setUploading(false);
        }
    };

    const { data: benefitData } = useQuery({
        queryKey: ['company-benefit', benefitId],
        queryFn: () => getCompanyBenefit(benefitId),
        enabled: isEditMode,
    });

    useEffect(() => {
        if (isEditMode && benefitData) {
            const b = benefitData;
            setForm({
                description: b.description || '',
                type: b.type || '',
                period: b.period || '',
                effectiveStart: toDateInput(b.effective || b.effectiveStart),
                effectiveEnd: toDateInput(b.effectiveEnd),
                companyContributionAmount: b.companyContributionAmount != null ? String(b.companyContributionAmount) : '',
                companyContributionPercent: b.companyContributionPercent != null ? String(b.companyContributionPercent) : '',
                employeeContributionAmount: b.employeeContributionAmount != null ? String(b.employeeContributionAmount) : '',
                employeeContributionPercent: b.employeeContributionPercent != null ? String(b.employeeContributionPercent) : '',
                totalPremiumAmount: b.totalPremiumAmount != null ? String(b.totalPremiumAmount) : '',
                preTax: b.preTax !== false,
                waitingPeriod: b.waitingPeriod || 'FirstOfMonthAfterHire',
                notes: (b.notes || '').replace(/\s*\|\s*documents:\[[\s\S]*?\](\s*\|)?/gi, '').replace(/^documents:\[[\s\S]*?\](\s*\|)?/gi, '').trim().replace(/\s*\|\s*$/, '').trim(),
            });
            // Load documents — prefer structured array, fall back to legacy single URL
            if (b.documents) {
                try { setDocuments(JSON.parse(b.documents)); } catch { /* ignore */ }
            } else if (b.documentUrl) {
                setDocuments([{ type: 'PlanDoc', url: b.documentUrl, name: 'Plan Document' }]);
            }
            if (b.companyContributionPercent) setCompanyMode('percent');
            if (b.employeeContributionPercent) setEmployeeMode('percent');
        }
    }, [isEditMode, benefitData]);

    const { mutate: mutateCreate, isPending: isCreating, isError: isCreateError, error: createError } = useMutation({
        mutationFn: createCompanyBenefit,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["company-benefits"] }); navigate("/benefits"); },
    });

    const { mutate: mutateUpdate, isPending: isUpdating, isError: isUpdateError, error: updateError } = useMutation({
        mutationFn: updateCompanyBenefit,
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["company-benefits"] }); navigate("/benefits"); },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleCompanyModeToggle = (mode) => {
        setCompanyMode(mode);
        setForm(prev => ({ ...prev, companyContributionAmount: '', companyContributionPercent: '' }));
    };

    const handleEmployeeModeToggle = (mode) => {
        setEmployeeMode(mode);
        setForm(prev => ({ ...prev, employeeContributionAmount: '', employeeContributionPercent: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.description.trim()) newErrors.description = "Plan name is required";
        if (!form.type) newErrors.type = "Benefit type is required";
        if (!form.period) newErrors.period = "Contribution period is required";
        if (!form.effectiveStart) newErrors.effectiveStart = "Start date is required";
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        const payload = {
            description: form.description,
            type: form.type,
            period: form.period,
            effectiveStart: new Date(form.effectiveStart).toISOString(),
        };
        if (form.effectiveEnd) payload.effectiveEnd = new Date(form.effectiveEnd).toISOString();
        if (companyMode === 'amount' && form.companyContributionAmount) payload.companyContributionAmount = parseFloat(form.companyContributionAmount);
        if (companyMode === 'percent' && form.companyContributionPercent) payload.companyContributionPercent = parseFloat(form.companyContributionPercent);
        if (employeeMode === 'amount' && form.employeeContributionAmount) payload.employeeContributionAmount = parseFloat(form.employeeContributionAmount);
        if (employeeMode === 'percent' && form.employeeContributionPercent) payload.employeeContributionPercent = parseFloat(form.employeeContributionPercent);
        if (employeeMode === 'percent' && form.totalPremiumAmount) payload.totalPremiumAmount = parseFloat(form.totalPremiumAmount);
        payload.preTax = form.preTax;
        payload.waitingPeriod = form.waitingPeriod;
        if (documents.length > 0) {
            payload.documents = JSON.stringify(documents);
            payload.documentUrl = documents[0].url; // keep legacy field for backward compat
        }
        if (form.notes) payload.notes = form.notes;
        if (isEditMode) mutateUpdate({ benefitId, ...payload });
        else mutateCreate(payload);
    };

    const isPending = isCreating || isUpdating;
    const isError = isCreateError || isUpdateError;
    const error = createError || updateError;

    return (
        <form className={cn("container-spacing pt-3 pb-5", styles.addEditCompanyBenefit)} onSubmit={handleSubmit}>
            <h3 className={cn("title mt-2")}>{isEditMode ? 'Edit Benefit Plan' : 'Add Benefit Plan'}</h3>
            <div>
                <p className={cn("info-title mt-3")}>General Information</p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-lg-8 col-12">
                            <Select label="Benefit Type*" fullWidth placeholder="Select benefit type" options={BENEFIT_TYPES} name="type" value={form.type} onChange={handleChange} error={!!errors.type} errorText={errors.type} />
                        </div>
                        <div className="col-lg-8 col-12">
                            <Input label="Plan Name*" fullWidth variant="flat" name="description" value={form.description} onChange={handleChange} error={!!errors.description} errorText={errors.description} placeholder="e.g. Blue Cross Medical Plan" />
                        </div>
                    </div>
                    <div className={cn("row g-3 mt-2")}>
                        <div className="col-lg-4 col-12">
                            <Input label="Start Date*" fullWidth variant="flat" type="date" name="effectiveStart" value={form.effectiveStart} onChange={handleChange} error={!!errors.effectiveStart} errorText={errors.effectiveStart} />
                        </div>
                        <div className="col-lg-4 col-12">
                            <Input label="End Date" fullWidth variant="flat" type="date" name="effectiveEnd" value={form.effectiveEnd} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Contribution Defaults</p>
                <p style={{ fontSize: 13, color: '#888', marginTop: -8, marginBottom: 12 }}>
                    These are the default contribution amounts used when enrolling employees. They can be overridden per employee.
                </p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row")}>
                        <div className="col-lg-4 col-12">
                            <Select label="Contribution Period*" fullWidth placeholder="Select period" options={BENEFIT_PERIODS} name="period" value={form.period} onChange={handleChange} error={!!errors.period} errorText={errors.period} />
                        </div>
                    </div>
                    <div className={cn("row g-4 mt-1")}>
                        <div className="col-lg-4 col-12">
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Company Contribution</div>
                            <ContribToggle mode={companyMode} onToggle={handleCompanyModeToggle} />
                            {companyMode === 'amount'
                                ? <Input label="Amount ($)" fullWidth variant="flat" type="number" placeholder="0.00" name="companyContributionAmount" value={form.companyContributionAmount} onChange={handleChange} />
                                : <Input label="Percent (%)" fullWidth variant="flat" type="number" placeholder="0.00" name="companyContributionPercent" value={form.companyContributionPercent} onChange={handleChange} />
                            }
                        </div>
                        <div className="col-lg-4 col-12">
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Employee Contribution</div>
                            <ContribToggle mode={employeeMode} onToggle={handleEmployeeModeToggle} />
                            {employeeMode === 'amount'
                                ? <Input label="Amount ($)" fullWidth variant="flat" type="number" placeholder="0.00" name="employeeContributionAmount" value={form.employeeContributionAmount} onChange={handleChange} />
                                : <>
                                    <Input label="Percent (%)" fullWidth variant="flat" type="number" placeholder="0.00" name="employeeContributionPercent" value={form.employeeContributionPercent} onChange={handleChange} />
                                    <div style={{ marginTop: 10 }}>
                                        <Input label="Total Premium / Period ($)" fullWidth variant="flat" type="number" placeholder="e.g. 50.00" name="totalPremiumAmount" value={form.totalPremiumAmount} onChange={handleChange} />
                                        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                                            Full plan cost per period (employer + employee). Employee deduction = premium × percent.
                                            <br/>Example: $50 premium × 20% = $10/period deducted from paycheck.
                                        </p>
                                    </div>
                                  </>
                            }
                        </div>
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Tax & Eligibility</p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-4")}>
                        <div className="col-lg-4 col-12">
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>
                                Tax Treatment
                                <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>(affects W-2 &amp; paycheck)</span>
                            </div>
                            <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                <button type="button"
                                    style={{ padding: '6px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: form.preTax !== false ? '#16a34a' : '#f3f4f6', color: form.preTax !== false ? '#fff' : '#555' }}
                                    onClick={() => setForm(p => ({ ...p, preTax: true }))}>
                                    Pre-tax
                                </button>
                                <button type="button"
                                    style={{ padding: '6px 18px', fontSize: 13, fontWeight: 600, border: 'none', borderLeft: '1px solid #dee2e6', cursor: 'pointer', background: form.preTax === false ? '#6b7280' : '#f3f4f6', color: form.preTax === false ? '#fff' : '#555' }}
                                    onClick={() => setForm(p => ({ ...p, preTax: false }))}>
                                    Post-tax
                                </button>
                            </div>
                            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                {form.preTax !== false ? 'Reduces taxable income — reported on W-2 Box 12.' : 'Deducted after taxes — no W-2 exclusion.'}
                            </p>
                        </div>
                        <div className="col-lg-4 col-12">
                            <Select
                                label="Eligibility Waiting Period"
                                fullWidth
                                placeholder="Select waiting period"
                                options={WAITING_PERIODS}
                                name="waitingPeriod"
                                value={form.waitingPeriod}
                                onChange={handleChange}
                            />
                            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>How long a new hire waits before coverage begins. ACA max is 90 days.</p>
                        </div>
                    </div>
                </div>

                <p className={cn("info-title mt-4")}>Plan Documents &amp; Notes</p>
                <div className={cn("container-fluid padding-0")}>
                    <div className={cn("row g-3")}>
                        <div className="col-12">
                            <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
                                Plan Documents <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional — employees can view these during enrollment)</span>
                            </label>

                            {/* Existing docs */}
                            {documents.map((doc, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '7px 10px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1B75F0', background: '#EFF6FF', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                                        {DOC_TYPES.find(d => d.value === doc.type)?.label.split('(')[0].trim() || doc.type}
                                    </span>
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        📄 {doc.name || doc.url}
                                    </a>
                                    <button type="button" onClick={() => setDocuments(prev => prev.filter((_, i) => i !== idx))}
                                        style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                                        Remove
                                    </button>
                                </div>
                            ))}

                            {/* Add new doc row */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 4 }}>
                                <select value={newDocType} onChange={e => setNewDocType(e.target.value)}
                                    style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, color: '#374151', background: '#fff', minWidth: 200 }}>
                                    {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                </select>
                                <input
                                    type="text"
                                    placeholder="Document name (e.g. Aetna PPO SBC 2026)"
                                    value={newDocName}
                                    onChange={e => setNewDocName(e.target.value)}
                                    style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, flex: 1, minWidth: 160 }}
                                />
                                <div style={{ display: 'flex', gap: 6, flex: 2, minWidth: 200 }}>
                                    <input
                                        type="url"
                                        placeholder="https://... or upload a file →"
                                        value={newDocUrl}
                                        onChange={e => setNewDocUrl(e.target.value)}
                                        style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, flex: 1 }}
                                    />
                                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg" ref={fileInputRef} style={{ display: 'none' }}
                                        onChange={e => { handleFileUpload(e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                        title="Upload a file"
                                        style={{ padding: '8px 12px', background: '#f3f4f6', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', color: '#374151' }}>
                                        {uploading ? '⏳' : '📎 Upload'}
                                    </button>
                                </div>
                                <button type="button"
                                    disabled={!newDocUrl.trim()}
                                    onClick={() => {
                                        if (!newDocUrl.trim()) return;
                                        setDocuments(prev => [...prev, { type: newDocType, url: newDocUrl.trim(), name: newDocName.trim() || newDocType }]);
                                        setNewDocUrl(''); setNewDocName('');
                                    }}
                                    style={{ padding: '8px 16px', background: newDocUrl.trim() ? '#1B75F0' : '#e5e7eb', color: newDocUrl.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: newDocUrl.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
                                    + Add
                                </button>
                            </div>
                            {uploadError && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{uploadError}</p>}
                            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Upload a file or paste a URL. Employees will see labeled links in their enrollment portal.</p>
                        </div>
                        <div className="col-lg-8 col-12">
                            <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 }}>Internal Notes</label>
                            <textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleChange}
                                rows={3}
                                placeholder="e.g. 401k match formula, carrier info, coverage details..."
                                style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 6, padding: '8px 12px', fontSize: 13, resize: 'vertical', color: '#333' }}
                            />
                            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Visible to admins only — not shown to employees.</p>
                        </div>
                    </div>
                </div>

                <div className="mt-5">
                    {isError && (
                        <p className="text-danger mb-2">{error?.response?.data?.message || error?.response?.data?.title || "Failed to save benefit plan. Please try again."}</p>
                    )}
                    <Button type="submit" disabled={isPending} className="me-3">{isPending ? "Saving..." : (isEditMode ? "Update Benefit Plan" : "Add Benefit Plan")}</Button>
                    <Button variant="secondary" type="button" onClick={() => navigate("/benefits")}>Cancel</Button>
                </div>
            </div>
        </form>
    );
};

export default AddEditCompanyBenefit;
